/**
 * Parse Collect&Go pack sizes and convert Menu quantities → cart quantities.
 *
 * Learnings from manual PDP flows (wortel / champignon / gehakt / cola):
 * - Packed bags (/st, pack in title): quantity is integer packs; no unit-type toggle.
 * - Weight /kg (`W` in unitTypes): quantity is decimal kg (min often 0.1).
 * - Dual produce: per st / per kg after add.
 * - Multipack drinks: per st / per pak (`N × M stuk`).
 * - Header cart badge = number of line items, not total units.
 */

export type PackSize = {
  weightConversionFactor: number | null
  packSize: number | null
  unitLabel: string | null
}

/**
 * Promo quantity thresholds — not pack size.
 * e.g. "€ 1,09/st vanaf 3 st", "+ 3st", "voeg 3 stuks toe … promoprijs"
 */
export function isPromoPieceThreshold(text: string): boolean {
  const t = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  if (!t) return false
  return (
    /\bvanaf\s+\d+\s*(?:stuks?|st)\b/.test(t) ||
    /\/\s*st\s+vanaf\b/.test(t) ||
    /\bpromoprijs\b/.test(t) ||
    /\bvoeg\s+\d+\s+stuks?\s+toe\b/.test(t) ||
    /^\+\s*\d+\s*(?:stuks?|st)\s*$/.test(t)
  )
}

/** Parse "1,5 kg", "500g", "6x20cl", "1L" from product titles / labels. */
export function parsePackSize(text: string): PackSize {
  const raw = String(text || '')
  const norm = raw.replace(',', '.').toLowerCase()

  // Nx packs: "6x20cl", "2x125g" (bloedworst), "6 x 200 ml"
  // n = pieces/units in the sell pack; content size is for weight/volume needs.
  const multi = norm.match(
    /(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(kg|g|l|cl|ml|st|stuk)/i
  )
  if (multi) {
    const n = Number(multi[1])
    const v = Number(multi[2])
    const u = multi[3]!.toLowerCase()
    if (u === 'st' || u === 'stuk') {
      return {
        weightConversionFactor: null,
        packSize: n * (v || 1),
        unitLabel: `${n}×${v}st`,
      }
    }
    const grams = toGrams(v, u)
    if (grams != null) {
      return {
        weightConversionFactor: grams * n,
        packSize: n > 1 ? n : null,
        unitLabel: `${n}×${v}${u}`,
      }
    }
  }

  const m = norm.match(/(\d+(?:\.\d+)?)\s*(kg|g|l|cl|ml)\b/i)
  if (m) {
    const v = Number(m[1])
    const u = m[2]!.toLowerCase()
    const grams = toGrams(v, u)
    return {
      weightConversionFactor: grams,
      packSize: null,
      unitLabel: `${v}${u}`,
    }
  }

  // Piece count in title (eieren "6st" / "12 stuks") — subunits per sell
  // unit, not a multipack pak. Never promo "vanaf 3 st" / "+ 3st".
  if (!isPromoPieceThreshold(norm)) {
    const pieces = norm.match(/(\d+)\s*(?:stuks?|st)\b/i)
    if (pieces) {
      const n = Number(pieces[1])
      if (n > 0) {
        return {
          weightConversionFactor: null,
          packSize: n,
          unitLabel: `${n}st`,
        }
      }
    }
  }

  if (/\bper\s*stuk\b|\/st\b/i.test(norm)) {
    return { weightConversionFactor: null, packSize: 1, unitLabel: 'st' }
  }

  return { weightConversionFactor: null, packSize: null, unitLabel: null }
}

function toGrams(value: number, unit: string): number | null {
  switch (unit) {
    case 'kg':
      return value * 1000
    case 'g':
      return value
    case 'l':
      return value * 1000 // treat L ≈ kg water for produce liquids
    case 'cl':
      return value * 10
    case 'ml':
      return value
    default:
      return null
  }
}

/**
 * How many Cogo pack units to add for a Menu line (integer packs / pieces).
 * Returns null if we cannot safely convert (caller should ask the user).
 * Prefer {@link cartQuantity} when driving the PDP quantity input.
 */
export function unitsToAdd(opts: {
  neededQty: number
  neededUnit: string
  weightConversionFactor?: number | null
  packSize?: number | null
}): number | null {
  const q = cartQuantity({ ...opts, unitType: null })
  if (!q || q.unit !== 'st') return null
  return Math.max(1, Math.round(q.value))
}

/** Collect&Go `unitType`: piece / weight / pack. */
export type UnitType = 'P' | 'W' | 'PA'

export function unitTypeLabel(mode: UnitType | null | undefined): string {
  if (mode === 'P') return 'per stuk'
  if (mode === 'W') return 'per kg'
  if (mode === 'PA') return 'per pak'
  return ''
}

/** Prefer piece → kg → pack (Menu recipes usually count produce). */
const UNIT_TYPE_RANK: Record<UnitType, number> = { P: 0, W: 1, PA: 2 }

export function sortUnitTypes(modes: UnitType[]): UnitType[] {
  return [...new Set(modes)].sort(
    (a, b) => UNIT_TYPE_RANK[a] - UNIT_TYPE_RANK[b]
  )
}

/** Primary CTA for the preferred unit type (piece > kg). */
export const unitTypePrimaryBtnClass =
  'rounded-full bg-green-600 hover:bg-green-500 active:bg-green-700 text-white px-6 py-2.5 text-sm font-medium tracking-wide disabled:opacity-40 disabled:pointer-events-none transition-colors'

/** Secondary CTA for alternate unit types. */
export const unitTypeSecondaryBtnClass =
  'rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 ring-1 ring-slate-300 px-4 py-2 text-sm font-medium disabled:opacity-40 disabled:pointer-events-none transition-colors'

/**
 * Primary CTA modes on a choice card.
 * Pack is demoted to a notice when piece/weight is also available.
 */
export function choiceUnitTypeButtons(modes: UnitType[]): UnitType[] {
  const sorted = sortUnitTypes(modes)
  if (sorted.some((m) => m === 'W' || m === 'P')) {
    return sorted.filter((m) => m !== 'PA')
  }
  return sorted
}

/** True when pack should show as a secondary notice (not a primary button). */
export function choicePackUnitNotice(modes: UnitType[]): boolean {
  return modes.includes('PA') && (modes.includes('W') || modes.includes('P'))
}

/**
 * Unit types that require a user pick on the choice card.
 * Fixed /st packs (a weightConversionFactor bag, or packSize > 1 like `20st` /
 * `6st`) are a single path even if scrape tagged €/kg — that strapline is unit
 * price, not a dual CTA. Dual loose produce may still carry a
 * weightConversionFactor as average piece weight — keep the st/kg picker there.
 * Stored unitTypes should still be `['P']` (or `['P','PA']`); this helper only
 * decides whether the UI shows a unit-type picker.
 * Order: P → W → PA for dual produce. UI demotes PA via {@link choiceUnitTypeButtons}.
 */
export function choiceUnitTypes(opt: {
  unitTypes?: UnitType[] | null
  weightConversionFactor?: number | null
  packSize?: number | null
}): UnitType[] {
  const modes = (opt.unitTypes || []).filter(
    (m): m is UnitType => m === 'P' || m === 'W' || m === 'PA'
  )
  // Piece-count packs (6st pears, 20st pasteitjes) — never dual st/kg CTA.
  if (opt.packSize != null && opt.packSize > 1) {
    if (modes.includes('PA') && modes.includes('P')) {
      return ['P', 'PA']
    }
    return []
  }
  // Dual loose produce: weightConversionFactor may be average piece weight.
  // Prefer per st as primary — Menu recipes usually count pieces (courgette,
  // paprika “2 st”); leading with per kg made users map W and sync kg.
  if (modes.includes('P') && modes.includes('W')) {
    return modes.includes('PA') ? ['P', 'W', 'PA'] : ['P', 'W']
  }
  const fixedGrams =
    opt.weightConversionFactor != null && opt.weightConversionFactor > 0
  if (fixedGrams) {
    if (modes.includes('PA') && modes.includes('P')) {
      return ['P', 'PA']
    }
    return []
  }
  return sortUnitTypes(modes)
}

/**
 * Runtime unit type for add-to-winkelwagen when map.unit_type is null.
 * Single `P` / empty → null (pack-/st math via cartQuantity).
 * Single `W` / `PA` → that type. Multi → soft pick from needed unit.
 *
 * Exception: Menu tracks pieces (`stuk`) but scrape only stored `W`
 * (common for dual produce whose shelf price is €/kg, e.g. aubergine).
 * Prefer `P` — pieces→kg needs an average weight we do not have.
 */
export function chooseUnitType(
  neededUnit: string,
  unitTypes?: UnitType[] | null
): UnitType | null {
  if (!unitTypes || unitTypes.length === 0) return null
  const unit = (neededUnit || '').toLowerCase()
  const wantWeight =
    unit.includes('gram') ||
    unit === 'g' ||
    unit === 'kg' ||
    unit.includes('kilogram')
  const wantPieces = isPieceUnit(neededUnit)
  if (unitTypes.length === 1) {
    const only = unitTypes[0]!
    if (only === 'P') return null
    if (only === 'W' && wantPieces) return 'P'
    return only
  }
  if (wantWeight && unitTypes.includes('W')) return 'W'
  if (wantPieces && unitTypes.includes('P')) return 'P'
  if (wantPieces && unitTypes.includes('W')) return 'P'
  // Soft default: piece → kg → pack. Prefer P when ambiguous so counted
  // produce (aubergine “3”) never becomes “3 kg” from a shelf-/kg map flag.
  if (unitTypes.includes('P')) return 'P'
  if (unitTypes.includes('W')) return 'W'
  if (unitTypes.includes('PA')) return 'PA'
  return unitTypes[0] ?? null
}

/**
 * Parse a gram quantum from labels like `100g`, `±100g`, `1,5kg` (→ null for kg).
 * Used as Cogo /kg min+step when dual-produce scrapes leave weightStepGrams=10.
 */
export function parseGramsFromUnitLabel(
  label: string | null | undefined
): number | null {
  const raw = String(label || '')
    .replace(',', '.')
    .trim()
  // Only plain gram labels — not "per st", "1.5kg", "6st".
  const m = raw.match(/^±?\s*(\d+(?:\.\d+)?)\s*g\b/i)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Grams step (and minimum) for Collect&Go /kg ordering.
 * Prefer an explicit `100g`-style unit label over a dual-produce 10g scrape default
 * (gember: need 30g → order 0,1 kg).
 */
export function resolveWeightStepGrams(opts: {
  weightStepGrams?: number | null
  weightConversionFactor?: number | null
  unitLabel?: string | null
}): number {
  const labeled = parseGramsFromUnitLabel(opts.unitLabel)
  if (labeled != null && labeled >= 50) return Math.round(labeled)
  if (opts.weightStepGrams != null && opts.weightStepGrams > 0) {
    return opts.weightStepGrams
  }
  // Small WCF as sell quantum (≤100g); larger values are average piece weights.
  if (
    opts.weightConversionFactor != null &&
    opts.weightConversionFactor > 0 &&
    opts.weightConversionFactor <= 100
  ) {
    return opts.weightConversionFactor
  }
  return 100
}

/** Apply unit type to pack fields used by unitsToAdd. */
export function packForUnitType(
  mode: UnitType | null,
  article: {
    weightConversionFactor?: number | null
    packSize?: number | null
    weightStepGrams?: number | null
    unitLabel?: string | null
  }
): {
  weightConversionFactor: number | null
  packSize: number | null
  unitLabel: string
} {
  if (mode === 'P') {
    return {
      weightConversionFactor: null,
      packSize: 1,
      unitLabel: 'per st',
    }
  }
  if (mode === 'W') {
    const step = resolveWeightStepGrams(article)
    return {
      weightConversionFactor: step,
      packSize: null,
      unitLabel: `${step}g`,
    }
  }
  if (mode === 'PA') {
    const pieces =
      article.packSize != null && article.packSize > 0 ? article.packSize : 1
    return {
      weightConversionFactor: null,
      packSize: pieces,
      unitLabel: `pak (${pieces}st)`,
    }
  }
  return {
    weightConversionFactor: article.weightConversionFactor ?? null,
    packSize: article.packSize ?? null,
    unitLabel: article.unitLabel || '',
  }
}

export type CartQuantity = {
  /** Literal value for the Cogo quantity input. */
  value: number
  /** PDP input unit: packs/pieces vs kilograms. */
  unit: 'st' | 'kg'
  /** Log-friendly label. */
  label: string
}

/**
 * Menu quantity → grams / ml-equivalent (density 1).
 * Liquids (ml/cl/l) share the same scale as weight so pack math works for
 * briks (`25cl` → 250) against recipe needs in ml.
 */
function neededGrams(qty: number, unit: string): number | null {
  const u = (unit || '').toLowerCase().trim()
  if (u.includes('gram') || u === 'g') return qty
  if (u === 'kg' || u.includes('kilogram')) return qty * 1000
  if (u === 'ml' || u.includes('milliliter') || u.includes('millilitre')) {
    return qty
  }
  if (u === 'cl' || u.includes('centiliter') || u.includes('centilitre')) {
    return qty * 10
  }
  if (u === 'l' || u === 'liter' || u === 'litre' || u.includes('liter')) {
    return qty * 1000
  }
  return null
}

/** Menu product units that mean countable pieces (not weight/volume). */
export function isPieceMenuUnit(unit: string): boolean {
  const u = (unit || '').toLowerCase().trim()
  return (
    u.includes('stuk') ||
    u === 'st' ||
    u === '' ||
    u === 'piece' ||
    u === 'pieces' ||
    u === 'pc' ||
    u === 'pcs' ||
    u === 'blik' ||
    u === 'blikje' ||
    u === 'can' ||
    u === 'tin'
  )
}

/** @deprecated use {@link isPieceMenuUnit} */
function isPieceUnit(unit: string): boolean {
  return isPieceMenuUnit(unit)
}

function formatKg(kg: number): string {
  const rounded = Math.round(kg * 1000) / 1000
  if (Number.isInteger(rounded)) return String(rounded)
  return String(rounded)
}

/**
 * Quantity to type into Collect&Go’s quantity selector.
 * Packed /st → integer packs. Loose /kg → decimal kilograms (rounded up to step).
 */
export function cartQuantity(opts: {
  neededQty: number
  neededUnit: string
  weightConversionFactor?: number | null
  packSize?: number | null
  unitType?: UnitType | null
  weightStepGrams?: number | null
  unitLabel?: string | null
}): CartQuantity | null {
  const qty = Number(opts.neededQty)
  if (!(qty > 0)) return null
  const grams = neededGrams(qty, opts.neededUnit)

  if (opts.unitType === 'P') {
    if (isPieceUnit(opts.neededUnit)) {
      const packP =
        opts.packSize != null && opts.packSize > 1 ? opts.packSize : 1
      const n = Math.max(1, Math.ceil(qty / packP))
      return {
        value: n,
        unit: 'st',
        label:
          packP > 1 ? `${n}× ${packP}st` : `${n}× per st`,
      }
    }
    // Weight/volume need → packs via content size (25cl cream, 250g bag).
    if (
      grams != null &&
      opts.weightConversionFactor != null &&
      opts.weightConversionFactor > 0
    ) {
      const n = Math.max(1, Math.ceil(grams / opts.weightConversionFactor))
      const packLabel = opts.unitLabel || `${opts.weightConversionFactor}g`
      return { value: n, unit: 'st', label: `${n}× ${packLabel}` }
    }
    // Unknown pack size: one piece (best-effort).
    return { value: 1, unit: 'st', label: '1× per st' }
  }

  if (opts.unitType === 'W') {
    if (grams == null) return null
    const step = resolveWeightStepGrams(opts)
    const orderedG = Math.max(step, Math.ceil(grams / step) * step)
    const kg = orderedG / 1000
    return {
      value: kg,
      unit: 'kg',
      label: `${formatKg(kg)} kg`,
    }
  }

  if (opts.unitType === 'PA') {
    const packP = opts.packSize != null && opts.packSize > 0 ? opts.packSize : 1
    if (isPieceUnit(opts.neededUnit)) {
      const n = Math.max(1, Math.ceil(qty / packP))
      return { value: n, unit: 'st', label: `${n}× pak` }
    }
    if (
      grams != null &&
      opts.weightConversionFactor != null &&
      opts.weightConversionFactor > 0
    ) {
      const n = Math.max(1, Math.ceil(grams / opts.weightConversionFactor))
      return { value: n, unit: 'st', label: `${n}× pak` }
    }
    return { value: 1, unit: 'st', label: '1× pak' }
  }

  // Packed bag sold /st with pack size in title (wortel 1,5kg, champignon 250g).
  if (
    grams != null &&
    opts.weightConversionFactor != null &&
    opts.weightConversionFactor > 0
  ) {
    const n = Math.max(1, Math.ceil(grams / opts.weightConversionFactor))
    const packLabel = opts.unitLabel || `${opts.weightConversionFactor}g`
    return { value: n, unit: 'st', label: `${n}× ${packLabel}` }
  }

  if (isPieceUnit(opts.neededUnit)) {
    const packP = opts.packSize != null && opts.packSize > 0 ? opts.packSize : 1
    const n = Math.max(1, Math.ceil(qty / packP))
    return { value: n, unit: 'st', label: `${n}×` }
  }

  return null
}

/** Prefer pack that covers needed grams with least overshoot (then fewer units). */
export function bestPackForWeight<
  T extends { weightConversionFactor?: number | null },
>(candidates: T[], neededGramsValue: number): T | null {
  if (!(neededGramsValue > 0)) return null
  const scored = candidates
    .filter(
      (c) => c.weightConversionFactor != null && c.weightConversionFactor > 0
    )
    .map((c) => {
      const pack = c.weightConversionFactor!
      const units = Math.max(1, Math.ceil(neededGramsValue / pack))
      return {
        c,
        units,
        overshoot: units * pack - neededGramsValue,
      }
    })
  if (!scored.length) return null
  scored.sort((a, b) => a.overshoot - b.overshoot || a.units - b.units)
  return scored[0]!.c
}

export type PackComboPick<T> = { article: T; count: number }

/**
 * Cheapest combination of pack sizes covering ≥ neededGrams.
 * Uses sell-unit cost (respects backendUnit=kg × weightConversionFactor); otherwise minimizes grams.
 */
export function cheapestPackCombo<
  T extends {
    weightConversionFactor?: number | null
    priceCents?: number | null
    backendUnit?: string | null
  },
>(candidates: T[], neededGramsValue: number): PackComboPick<T>[] | null {
  if (!(neededGramsValue > 0)) return null
  const packs = candidates
    .map((article, index) => {
      const grams = article.weightConversionFactor ?? 0
      let price: number | null = null
      if (article.priceCents != null && article.priceCents > 0) {
        if (article.backendUnit === 'kg') {
          if (grams > 0) {
            price = Math.round((article.priceCents * grams) / 1000)
          }
        } else {
          price = article.priceCents
        }
      }
      return { article, index, grams, price }
    })
    .filter((p) => p.grams > 0)
  if (!packs.length) return null

  const anyPriced = packs.some((p) => p.price != null)
  const unitCost = (p: (typeof packs)[number]) => {
    if (anyPriced) {
      return p.price != null ? p.price : Number.POSITIVE_INFINITY
    }
    return p.grams
  }
  if (anyPriced && packs.every((p) => !Number.isFinite(unitCost(p)))) {
    return null
  }

  const maxPack = Math.max(...packs.map((p) => p.grams))
  const targetMax = Math.ceil(neededGramsValue + maxPack)
  const INF = Number.POSITIVE_INFINITY
  const cost = new Array<number>(targetMax + 1).fill(INF)
  const via = new Array<number>(targetMax + 1).fill(-1)
  const prevG = new Array<number>(targetMax + 1).fill(-1)
  cost[0] = 0

  for (let g = 0; g <= targetMax; g++) {
    if (cost[g] === INF) continue
    for (let i = 0; i < packs.length; i++) {
      const p = packs[i]!
      const c = unitCost(p)
      if (!Number.isFinite(c)) continue
      const ng = g + p.grams
      if (ng > targetMax) continue
      const nc = cost[g] + c
      if (nc < cost[ng]) {
        cost[ng] = nc
        via[ng] = i
        prevG[ng] = g
      }
    }
  }

  let bestG = -1
  let bestC = INF
  for (let g = Math.ceil(neededGramsValue); g <= targetMax; g++) {
    if (cost[g] < bestC) {
      bestC = cost[g]
      bestG = g
    }
  }
  if (bestG < 0 || bestC === INF) return null

  const counts = new Map<number, number>()
  let cur = bestG
  while (cur > 0) {
    const i = via[cur]!
    if (i < 0) break
    counts.set(i, (counts.get(i) || 0) + 1)
    cur = prevG[cur]!
  }

  const out: PackComboPick<T>[] = []
  for (const [i, count] of counts) {
    out.push({ article: packs[i]!.article, count })
  }
  out.sort(
    (a, b) =>
      (b.article.weightConversionFactor || 0) -
      (a.article.weightConversionFactor || 0)
  )
  return out.length ? out : null
}

export function menuNeededGrams(qty: number, unit: string): number | null {
  return neededGrams(qty, unit)
}

/** Convert a winkelwagen line qty into Menu grams when possible. */
export function cartLineToGrams(opts: {
  quantity: number
  quantityUnit: 'st' | 'kg'
  weightConversionFactor?: number | null
  packSize?: number | null
  unitType?: UnitType | null
}): number | null {
  const q = Number(opts.quantity)
  if (!(q > 0)) return null
  if (opts.quantityUnit === 'kg' || opts.unitType === 'W') {
    return q * 1000
  }
  if (opts.weightConversionFactor != null && opts.weightConversionFactor > 0) {
    return q * opts.weightConversionFactor
  }
  return null
}

/** Convert cart line into Menu piece-count when possible. */
export function cartLineToPieces(opts: {
  quantity: number
  quantityUnit: 'st' | 'kg'
  packSize?: number | null
  unitType?: UnitType | null
}): number | null {
  const q = Number(opts.quantity)
  if (!(q > 0)) return null
  if (opts.quantityUnit === 'kg') return null
  if (opts.unitType === 'PA') {
    const packP = opts.packSize != null && opts.packSize > 0 ? opts.packSize : 1
    return q * packP
  }
  if (opts.packSize != null && opts.packSize > 1) {
    return q * opts.packSize
  }
  return q
}
